import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { format, parseISO, differenceInCalendarDays } from "date-fns";
import { de } from "date-fns/locale";
import {
  UserCheck, AlertTriangle, Check, Search, X, Ban, History, CalendarCheck,
  Loader2, Users, MapPin, Calendar as CalIcon, Download,
  CircleDashed, CheckCircle2,
} from "lucide-react";
import api, { formatErr } from "../lib/api";
import { useAuth } from "../lib/auth";
import { BRAND_COLORS, STATUS_COLORS, withAlpha, bookingPill, taskStatusColor, hexToStatusKey } from "../lib/bookingStatus";
import { StatusBadge } from "../components/StatusBadge";
import BookingDetailModal from "../components/BookingDetailModal";
import RequestDetailModal from "../components/RequestDetailModal";
import { SeenDot, SeenFilterToggle, isUnseenAssignment } from "../components/SeenIndicator";
import { AdminPinPromptDialog } from "../components/admin/AdminPinPromptDialog";

/**
 * Feb 2026 · Team-Bookings-Übersicht mit Multi-Select-Zuweisung.
 *
 * User-Directive: „Erlaub dem Admin, mehrere unassigned Buchungen
 * gleichzeitig einem Concierge zuzuweisen — analog zu Objekten."
 *
 * Anzeige:
 *   • Standard-Filter „Nicht zugewiesene Buchungen" (voreingestellt aktiv).
 *   • Multi-Select-Checkboxen (Admin-only) → Sticky-Action-Bar am unteren
 *     Rand mit Concierge/Admin-Dropdown und „Zuweisen"-Button.
 *   • Statusfarbe erscheint als Left-Border-Akzent (Grau ohne Assignee,
 *     Blau/Grün/Rot analog zur Grau→Blau-Regel).
 *   • Backend-Endpoint: `POST /api/team/bookings/bulk-assign`.
 */
export default function TeamBookings() {
  const { user, activeConciergeId, isImpersonatingConcierge, impersonatedConcierge } = useAuth();
  const isAdmin = user?.role === "admin";
  // Feb 2026 · 11 Sep 2026 · User-Directive „Filter Meine Buchungen":
  // Chip in der Buchungsliste, der auf die eigenen zugewiesenen Buchungen
  // filtert. Als Modifier gedacht — kombiniert mit dem aktiven Status-Modus
  // (z. B. „Meine + In Betreuung"). Ziel-ID: `activeConciergeId` in
  // Concierge-Ansicht (auch für Admin-Impersonation), sonst `user.id`.
  const myFilterId = activeConciergeId || user?.id || null;
  const [myOnly, setMyOnly] = useState(false);
  const [rows, setRows] = useState(null);
  const [members, setMembers] = useState([]);
  const [propertiesById, setPropertiesById] = useState({});
  const [extrasCatalog, setExtrasCatalog] = useState([]);
  // Feb 2026 · Directive #309: Row-Klick öffnet BookingDetailModal (nicht
  // mehr Selection-Toggle). Checkbox mit stopPropagation bleibt der Weg
  // für Bulk-Select. `selectedBooking` hält die aktuell offene Detail-
  // ansicht.
  const [selectedBooking, setSelectedBooking] = useState(null);
  // Feb 2026 · User-Directive „Klick öffnet Buchungsdetail, nicht
  // Kundenprofil": für merged Non-Absence-Rows (Extras, Request-Anything,
  // Team-Tasks, Buchungs-Änderungen) öffnen wir jetzt das
  // RequestDetailModal — kein window.location.href auf /team/customers/…
  // mehr. Der aktuell offene Request-Detail wird hier gehalten.
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [pickAssignee, setPickAssignee] = useState("");
  const [busy, setBusy] = useState(false);
  // Feb 2026 · 11 Sep 2026 · PIN-Gate für Bulk-Assign — der Admin muss
  // vor der Zuweisung einmal die PIN eingeben. `pinUnlocked` hält die
  // Freischaltung für weitere Bulk-Runs bis zum Reload.
  const [bulkPinUnlocked, setBulkPinUnlocked] = useState(false);
  const [bulkPinOpen, setBulkPinOpen] = useState(false);
  const [q, setQ] = useState("");
  // Feb 2026 · Directive #308: Drei mutually-exclusive Filter-Modi
  //   "upcoming"   → nicht storniert + Endedatum ≥ heute (Standard)
  //   "past"       → nicht storniert + Endedatum < heute
  //   "cancelled"  → nur stornierte Buchungen
  // „Ohne Verantwortlichen" ist kein eigener Modus mehr — Unassigned-
  // Buchungen erscheinen weiterhin oben im Upcoming-Modus (durch Sort),
  // damit der Bulk-Assign-Workflow intakt bleibt.
  const [filterMode, setFilterMode] = useState("upcoming");
  // Feb 2026 · Directive #312: Reporting-Filter für „Vergangene" —
  // Jahr + Quartal. „all" bedeutet keine Einschränkung. Nur aktiv, wenn
  // filterMode === "past".
  const [periodYear, setPeriodYear] = useState("all");
  const [periodQuarter, setPeriodQuarter] = useState("all");

  const load = async () => {
    try {
      const [b, m, ex, p] = await Promise.all([
        api.get("/bookings"),
        api.get("/team/members"),
        api.get("/extras").catch(() => ({ data: [] })),
        api.get("/team/properties").catch(() => ({ data: [] })),
      ]);
      const enriched = (b.data || []).map((row) => ({ ...row }));
      setRows(enriched);
      setMembers((m.data || []).filter((u) => ["admin", "concierge"].includes(u.role)));
      setExtrasCatalog(ex.data || []);
      // Feb 2026 · 11 Sep 2026 · Property-Map für Haupt-Concierge-Fallback.
      // Wenn eine Buchung `assigned_to` leer hat, aber das Objekt einen
      // Haupt-Concierge trägt, ist DAS der Verantwortliche — analog zum
      // BookingDetailModal.jsx („hasAnyResponsibility"-Zweig).
      const byId = {};
      for (const pp of (p.data || [])) byId[pp.id] = pp;
      setPropertiesById(byId);
    } catch (err) {
      toast.error(formatErr(err.response?.data?.detail) || err.message);
      setRows([]);
    }
  };
  useEffect(() => { load(); }, []);

  // Feb 2026 · User-Directive „Anfrage-Buchungs-Verlinkung im UI":
  // Deep-Link Support — `?open=<booking_id>` öffnet automatisch das
  // BookingDetailModal für die entsprechende Buchung, sobald die Liste
  // geladen ist. Nach dem Öffnen wird der Query-Param bereinigt, damit
  // ein Reload nicht erneut auto-öffnet. Kommt z.B. aus dem Chip in
  // RequestDetailModal (siehe `BookingLinkChip`).
  const [searchParams, setSearchParams] = useSearchParams();
  const unseenOnly = searchParams.get("unseen") === "1";
  const unseenViewer = activeConciergeId || (user?.role === "concierge" ? user?.id : null);
  // Feb 2026 · Sonderwunsch-Promotion Debug (5 Sep 2026 · User-Directive):
  // Wenn ein Deep-Link `?mode=<filterMode>` mitgibt, wechseln wir vor dem
  // Auto-Open des Modals in genau diese Kachel. Ansonsten kann es passieren,
  // dass ein frisch promovierter Sonderwunsch (Status "in_progress") unter
  // der Default-Kachel „Upcoming" liegt und der User ihn nicht findet,
  // wenn er die Detail-Ansicht in "In Betreuung" erwartet.
  useEffect(() => {
    const modeParam = searchParams.get("mode");
    const validModes = new Set(["upcoming", "past", "cancelled", "requested", "in_progress", "completed", "overdue"]);
    if (modeParam && validModes.has(modeParam)) {
      setFilterMode(modeParam);
      // Query-Param nicht löschen — der Auto-Open-Effect braucht `open`.
    }
  }, [searchParams]);
  useEffect(() => {
    if (!rows) return;
    const openId = searchParams.get("open");
    if (!openId) return;
    const target = rows.find((r) => r.id === openId);
    if (target) {
      setSelectedBooking(target);
      // Element zur besseren UX in die Sicht scrollen, ohne Modal-Fokus zu stören.
      setTimeout(() => {
        const el = document.querySelector(`[data-testid="team-booking-row-${openId}"]`);
        if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
      }, 200);
    } else {
      toast.error("Verknüpfte Buchung nicht in der aktuellen Liste gefunden — evtl. gefiltert oder gelöscht.");
    }
    const next = new URLSearchParams(searchParams);
    next.delete("open");
    setSearchParams(next, { replace: true });
  }, [rows, searchParams, setSearchParams]);

  // Enrich · Namen der Assignees (aus members-Map)
  const membersById = useMemo(
    () => Object.fromEntries(members.map((m) => [m.id, m])),
    [members],
  );

  // Client-side filter · Feb 2026 · Directive #308:
  //   „Vergangen" wird per `to_date < heute` bestimmt. Fehlt `to_date`
  //   wird die Buchung als „aktiv/zukünftig" behandelt (safer default).
  const today = useMemo(() => new Date().toISOString().slice(0, 10), []);
  const filtered = useMemo(() => {
    if (!rows) return null;
    const s = q.trim().toLowerCase();
    let base = rows;
    if (filterMode === "cancelled") {
      base = base.filter((r) => r.status === "cancelled");
    } else if (filterMode === "requested") {
      // Feb 2026 · Status-Kachel „Angefragt" — grau, neue Buchungen ohne
      // aktive Betreuung. Kein Zeitfenster-Constraint (auch vergangene,
      // wenn sie nie beantwortet wurden — dann liegt Handlungsbedarf).
      base = base.filter((r) => r.status === "requested");
    } else if (filterMode === "in_progress") {
      // „In Betreuung" — Blau. `confirmed` + dynamisch berechnetes `in_progress`.
      base = base.filter((r) => r.status === "confirmed" || r.status === "in_progress");
    } else if (filterMode === "completed") {
      base = base.filter((r) => r.status === "completed");
    } else if (filterMode === "overdue") {
      // 24 Jun 2026 · Rot = überfällig (to_date < heute, nicht erledigt/storniert)
      base = base.filter((r) => r.status !== "cancelled" && r.status !== "completed" && !!r.to_date && r.to_date < today);
    } else if (filterMode === "past") {
      base = base.filter((r) => r.status !== "cancelled"
        && r.to_date && r.to_date < today);
      // Feb 2026 · Directive #312: Zeitraum-Filter für Reporting
      if (periodYear !== "all") {
        base = base.filter((r) => (r.from_date || "").startsWith(periodYear));
      }
      if (periodQuarter !== "all") {
        const qMonths = {
          Q1: ["01", "02", "03"],
          Q2: ["04", "05", "06"],
          Q3: ["07", "08", "09"],
          Q4: ["10", "11", "12"],
        }[periodQuarter] || [];
        base = base.filter((r) => {
          const mm = (r.from_date || "").slice(5, 7);
          return qMonths.includes(mm);
        });
      }
    } else { // upcoming
      base = base.filter((r) => r.status !== "cancelled"
        && (!r.to_date || r.to_date >= today));
    }
    if (s) {
      base = base.filter((r) =>
        (r.property_name || "").toLowerCase().includes(s)
        || (r.customer_name || "").toLowerCase().includes(s)
        || (r.from_date || "").includes(s)
        || (r.to_date || "").includes(s)
      );
    }
    // Feb 2026 · 16 Sep 2026 · User-Directive „Admin-Impersonation zeigt
    // nur zugewiesene Daten": wenn der Admin die Perspektive eines
    // bestimmten Concierge einnimmt, dürfen NUR Buchungen erscheinen, an
    // denen dieser Concierge entweder direkter Verantwortlicher
    // (`assigned_to`) ODER Haupt-Concierge des Objekts
    // (`assigned_concierge_id`) ist — exakt die Logik, mit der auch das
    // Backend die eigene Concierge-Ansicht scoped (siehe
    // `list_bookings`/`concierge_main_property_ids`). Für echte
    // Concierges hat das Backend bereits gefiltert; der Client-Filter
    // ist dort ein No-Op.
    //
    // Nachtrag: `activeConciergeId` verlangt `effectiveRole === "concierge"`.
    // Manche Einstiegs-Pfade setzen jedoch nur `impersonatedConcierge` —
    // wir nehmen das direkte Signal als Fallback, damit der Filter in
    // jedem Impersonations-Fall greift.
    const _scopeId = isImpersonatingConcierge
      ? (activeConciergeId || impersonatedConcierge?.id || null)
      : null;
    if (_scopeId) {
      base = base.filter((r) =>
        r.assigned_to === _scopeId
        || r.assigned_concierge_id === _scopeId
      );
    }
    // Feb 2026 · 11 Sep 2026 · „Meine Buchungen"-Modifier. Nur exakter
    // Match auf `assigned_to === myFilterId` — Property-Fallback wird
    // bewusst NICHT einbezogen (der User will sehen, wofür er direkt
    // verantwortlich ist, nicht nur „Haupt-Concierge des Objekts").
    if (myOnly && myFilterId) {
      base = base.filter((r) => r.assigned_to === myFilterId);
    }
    if (unseenOnly) {
      base = base.filter((r) => r.status !== "completed" && r.status !== "cancelled" && isUnseenAssignment(r, unseenViewer, "assigned_to"));
    }
    // Sort: nicht zugewiesene zuerst (im Upcoming-Modus wichtig für Bulk-
    // Assign), dann chronologisch nach Startdatum. Vergangene absteigend.
    return base
      .slice()
      .sort((a, b) => {
        if (filterMode === "upcoming") {
          const ax = a.assigned_to ? 1 : 0;
          const bx = b.assigned_to ? 1 : 0;
          if (ax !== bx) return ax - bx;
          return (a.from_date || "").localeCompare(b.from_date || "");
        }
        if (filterMode === "past") {
          // Neueste zuerst
          return (b.from_date || "").localeCompare(a.from_date || "");
        }
        return (a.from_date || "").localeCompare(b.from_date || "");
      });
  }, [rows, q, filterMode, today, periodYear, periodQuarter, myOnly, myFilterId, unseenOnly, unseenViewer, isImpersonatingConcierge, activeConciergeId, impersonatedConcierge?.id]);

  // Feb 2026 · Directive #312 · Jahres-Liste für den Reporting-Filter.
  // Ableitung aus allen vergangenen Buchungen (falls die Liste leer ist,
  // zeigen wir wenigstens das aktuelle + Vorjahr als Fallback).
  const availableYears = useMemo(() => {
    const set = new Set();
    (rows || []).forEach((r) => {
      if (r.status === "cancelled") return;
      if (!r.to_date || r.to_date >= today) return;
      const y = (r.from_date || "").slice(0, 4);
      if (/^\d{4}$/.test(y)) set.add(y);
    });
    const list = Array.from(set).sort().reverse();
    if (list.length === 0) {
      const now = new Date().getFullYear();
      return [String(now), String(now - 1)];
    }
    return list;
  }, [rows, today]);

  const upcomingCount = useMemo(
    () => (rows || []).filter((r) => r.status !== "cancelled"
      && (!r.to_date || r.to_date >= today)).length,
    [rows, today],
  );
  const pastCount = useMemo(
    () => (rows || []).filter((r) => r.status !== "cancelled"
      && r.to_date && r.to_date < today).length,
    [rows, today],
  );
  const cancelledCount = useMemo(
    () => (rows || []).filter((r) => r.status === "cancelled").length,
    [rows],
  );
  // Feb 2026 · User-Directive „Status-Kacheln Angefragt/In Betreuung/Erledigt"
  // (1 Sep 2026): drei zusätzliche Filter-Kacheln, die die Buchungen
  // exklusiv auf den jeweiligen Status filtern. „In Betreuung" umfasst
  // sowohl `confirmed` (klassisch bestätigt) als auch `in_progress` (dynamisch
  // gesetzt, sobald mindestens ein Service erledigt ist).
  const requestedCount = useMemo(
    () => (rows || []).filter((r) => r.status === "requested").length,
    [rows],
  );
  const inProgressCount = useMemo(
    () => (rows || []).filter((r) => r.status === "confirmed" || r.status === "in_progress").length,
    [rows],
  );
  const completedCount = useMemo(
    () => (rows || []).filter((r) => r.status === "completed").length,
    [rows],
  );
  // Feb 2026 · 6 Sep 2026 · User-Directive „Angefragt-Kachel prominenter":
  // Kleine Zaehler-Chip oben, die alle unassignierten Buchungen (kein
  // Verantwortlicher) zeigt und beim Klick auf die „Neu / Angefragt"-
  // Filter-Kachel springt. Wir filtern auf `!assigned_to` (Zuteilung
  // fehlt) UND nicht-storniert — der Admin will nur die aktiven Faelle
  // sehen, die ihm noch auf dem Tisch liegen.
  const unassignedCount = useMemo(
    () => (rows || []).filter(
      (r) => !r.assigned_to && r.status !== "cancelled",
    ).length,
    [rows],
  );

  // 29 Jun 2026 · Punkt 23 · „Meine Buchungen"-Zähler an den aktiven
  // Status-Reiter angleichen. Der Zähler wird aus derselben `filtered`-
  // Grundmenge abgeleitet wie die Liste (also inkl. aktivem Status-Modus,
  // Zeitraum-, Such- und Impersonation-Filter). Dadurch gilt garantiert:
  // Zähler === Anzahl sichtbarer Zeilen, sobald „Meine Buchungen" aktiv ist —
  // in JEDEM Modus (anstehend, vergangen, angefragt, in Betreuung, erledigt,
  // storniert). Semantik unverändert: nur direkte `assigned_to`-Zuweisung,
  // kein Property-Fallback. (Ist `myOnly` bereits an, ist `filtered` schon
  // auf die eigenen Buchungen eingegrenzt — die Zählung bleibt identisch.)
  const myBookingsCount = useMemo(
    () => (myFilterId
      ? (filtered || []).filter((r) => r.assigned_to === myFilterId).length
      : 0),
    [filtered, myFilterId],
  );
  const unseenCount = useMemo(
    () => (rows || []).filter((r) => r.status !== "completed" && r.status !== "cancelled" && isUnseenAssignment(r, unseenViewer, "assigned_to")).length,
    [rows, unseenViewer],
  );
  const toggleUnseen = () => {
    const next = new URLSearchParams(searchParams);
    if (unseenOnly) next.delete("unseen"); else next.set("unseen", "1");
    setSearchParams(next, { replace: true });
  };

  const toggle = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const clearSelection = () => setSelectedIds(new Set());
  const selectAllVisible = () => {
    // Feb 2026 · Cancelled Buchungen sind nicht auswählbar (kein Assignee
    // mehr nötig) — Select-All überspringt sie.
    setSelectedIds(new Set((filtered || []).filter((r) => r.status !== "cancelled").map((r) => r.id)));
  };

  const submitBulk = async () => {
    const ids = Array.from(selectedIds);
    if (ids.length === 0) return;
    if (!pickAssignee) {
      toast.error("Bitte einen Verantwortlichen wählen.");
      return;
    }
    // Feb 2026 · 11 Sep 2026 · PIN-Gate. Bei mehrfacher Bulk-Zuweisung in
    // derselben Session (nach einmaligem Freischalten) bleibt der Prompt
    // aus — analog zu Projekt-/Rechnungs-Edits.
    if (!bulkPinUnlocked) {
      setBulkPinOpen(true);
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/team/bookings/bulk-assign", {
        booking_ids: ids,
        assigned_to: pickAssignee,
      });
      toast.success(
        `${data.updated} Buchung${data.updated === 1 ? "" : "en"} an ${data.assignee?.name || "—"} zugewiesen.`
      );
      clearSelection();
      setPickAssignee("");
      await load();
    } catch (err) {
      toast.error(formatErr(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  // Feb 2026 · Directive „Booking CSV Export" (P2). Nimmt die aktuell
  // gefilterte Liste (also exakt was der Admin auf dem Bildschirm sieht —
  // inkl. Suchbegriff + Filter-Modus + Jahres-/Quartalsauswahl) und lädt
  // sie als CSV runter. UTF-8-BOM, damit Excel Umlaute korrekt öffnet.
  // Dateiname trägt den Filter-Kontext, sodass Buchhaltung „Vergangene
  // 2026 Q3.csv" ohne Umbenennen ablegen kann.
  const KIND_LABEL_CSV = {
    absence: "Abwesenheit",
    extra_attached: "Extra (verknüpft)",
    extra_separate: "Extra (Standalone)",
    request_task: "Sonderwünsche",
    team_task: "Team-Aufgabe",
    change_request: "Buchungs-Änderung",
  };
  const STATUS_LABEL_CSV = {
    requested: "Angefragt",
    confirmed: "In Betreuung",
    in_progress: "In Betreuung",
    completed: "Erledigt",
    cancelled: "Storniert",
  };
  // Feb 2026 · User-Directive „Titel der Buchung in Listenzeile"
  // (3 Sep 2026): Der Buchungs-Titel ist direkt in der Liste sichtbar,
  // ohne dass das Detail-Modal geöffnet werden muss. Wir leiten den
  // Titel deterministisch ab:
  //   • Non-Absence (Extras/Request-Anything/Team-Task/Buchungs-Änderung):
  //     erster Eintrag aus `services` (Liste der sr-Titel), sonst Notiz
  //     oder Kind-Label.
  //   • Abwesenheit: erste Zeile aus `notes` (bis 90 Zeichen), sonst
  //     „Abwesenheit · N Nächte" auto-berechnet aus from/to.
  //   • Fallback auf das reine Kind-Label, wenn nichts vorhanden ist.
  const KIND_LABEL_UI = KIND_LABEL_CSV;
  const deriveBookingTitle = (b) => {
    const kind = b.booking_kind || "absence";
    if (kind !== "absence") {
      const svc = Array.isArray(b.services) && b.services.length ? b.services[0] : "";
      if (svc) return svc;
      const notes = (b.notes || "").split("\n")[0].trim();
      if (notes) return notes.length > 90 ? notes.slice(0, 90) + "…" : notes;
      return KIND_LABEL_UI[kind] || "Aufgabe";
    }
    // Absence
    const notes = (b.notes || "").split("\n")[0].trim();
    if (notes) return notes.length > 90 ? notes.slice(0, 90) + "…" : notes;
    let nights = null;
    try {
      if (b.from_date && b.to_date) {
        nights = differenceInCalendarDays(parseISO(b.to_date), parseISO(b.from_date));
      }
    } catch { /* ignore */ }
    if (typeof nights === "number" && nights > 0) {
      return `Abwesenheit · ${nights} ${nights === 1 ? "Nacht" : "Nächte"}`;
    }
    return "Abwesenheit";
  };
  const csvEscape = (val) => {
    if (val === null || val === undefined) return "";
    const s = String(val);
    if (/[",;\n\r]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
    return s;
  };
  const exportCsv = () => {
    if (!filtered || filtered.length === 0) {
      toast.error("Keine Buchungen zum Exportieren im aktuellen Filter.");
      return;
    }
    // Feb 2026 · 11 Sep 2026 · User-Directive „CSV-Export ergänzen: Nimm
    // den Haupt-Concierge als eigene Spalte in den Buchungs-Export auf,
    // damit die neue Sichtbarkeit auch reportfähig wird."
    //
    // Zwei getrennte Spalten spiegeln jetzt genau den Zweizeiler in der
    // Liste + Detail-Ansicht:
    //   • „Haupt-Concierge" — aus `assigned_concierge_id` des Objekts
    //     (Property-Fallback, immer objektbezogen)
    //   • „Verantwortlicher" — aus `assigned_to` der Buchung (strikt die
    //     direkte Zuweisung); leer wenn keine gesetzt ist. KEIN Property-
    //     Fallback in dieser Spalte — sonst würden Reports fälschlich den
    //     Haupt-Concierge als Verantwortlichen ausweisen (siehe Hotfix
    //     „VERANTWORTLICH zeigt immer den direkt der Buchung zugewiesenen").
    //   • „Verantw.-Herkunft" — leichte Ergänzung, damit Auswertungen
    //     „direkt zugewiesen" vs. „via Objekt" (Fallback im Alltag) sauber
    //     trennen können.
    const header = [
      "Buchungs-ID", "Kunde", "Objekt", "Von", "Bis", "Nächte",
      "Buchungsart", "Status",
      "Haupt-Concierge", "Verantwortlicher", "Verantw.-Rolle", "Verantw.-Herkunft",
      "Notizen",
    ];
    const lines = [header.map(csvEscape).join(";")];
    for (const b of filtered) {
      let nights = "";
      if (b.from_date && b.to_date) {
        try {
          const n = differenceInCalendarDays(parseISO(b.to_date), parseISO(b.from_date));
          if (Number.isFinite(n) && n >= 0) nights = String(n);
        } catch { /* skip */ }
      }
      // Haupt-Concierge des Objekts (immer Property-basiert)
      const propHauptId = propertiesById[b.property_id]?.assigned_concierge_id || null;
      const propHaupt = propHauptId ? (membersById[propHauptId] || null) : null;
      // Direkter Verantwortlicher der Buchung — Rohwert entscheidet, nicht
      // der Member-Lookup (Ex-Concierge, gelöschter User → wir wollen
      // trotzdem KEINEN Property-Fallback in dieser Spalte).
      const rawAssigneeId = b.assigned_to || null;
      const directAssignee = rawAssigneeId
        ? (membersById[rawAssigneeId] || null)
        : null;
      // Herkunft: „Direkt" wenn assigned_to gesetzt (auch wenn Name fehlt),
      // sonst „Via Objekt" wenn Property-Haupt existiert, sonst leer.
      const verantHerkunft = rawAssigneeId
        ? "Direkt"
        : (propHauptId ? "Via Objekt" : "");
      // Falls die direkte Zuweisung nicht auflösbar ist, geben wir eine
      // Kurz-ID mit, damit die Zeile nicht leer wirkt und der Owner die
      // Referenz nachschlagen kann.
      const verantName = directAssignee?.name
        || directAssignee?.email
        || (rawAssigneeId ? `Unbekannt (ID: ${String(rawAssigneeId).slice(0, 8)}…)` : "");
      // „Effektive" Herkunft der Rolle: direkt zugewiesen → dessen Rolle;
      // sonst leer (Property-Fallback befüllt Verantwortlicher NICHT).
      const verantRolle = directAssignee
        ? (directAssignee.role === "admin" ? "Admin" : "Concierge")
        : "";
      const row = [
        b.id,
        b.customer_name || b.customer_email || "",
        b.property_name || "",
        b.from_date || "",
        b.to_date || "",
        nights,
        KIND_LABEL_CSV[b.booking_kind || "absence"] || (b.booking_kind || ""),
        STATUS_LABEL_CSV[b.status] || (b.status || ""),
        propHaupt?.name || propHaupt?.email || "",
        verantName,
        verantRolle,
        verantHerkunft,
        (b.notes || "").replace(/\r?\n/g, " · "),
      ];
      lines.push(row.map(csvEscape).join(";"));
    }
    // Dateiname mit Filter-Kontext
    const modeSlug = filterMode === "past"
      ? "vergangene"
      : filterMode === "cancelled" ? "stornierte" : "aktive-zukuenftige";
    const periodBits = [];
    if (filterMode === "past" && periodYear !== "all") periodBits.push(periodYear);
    if (filterMode === "past" && periodQuarter !== "all") periodBits.push(periodQuarter);
    const stamp = new Date().toISOString().slice(0, 10);
    const fname = `weissblom-buchungen-${modeSlug}${periodBits.length ? "-" + periodBits.join("-") : ""}-stand-${stamp}.csv`;
    // BOM für Excel-UTF-8-Detection
    const blob = new Blob(["\uFEFF" + lines.join("\r\n")], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = fname;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    setTimeout(() => URL.revokeObjectURL(url), 1000);
    toast.success(`${filtered.length} Buchung${filtered.length === 1 ? "" : "en"} exportiert.`);
  };

  if (!isAdmin) {
    // Feb 2026 · User-Directive „Concierge muss seine eigenen offenen
    // Aufgaben vollständig einsehen und bearbeiten können".
    // Statt harter Sperre: Concierges landen auf DERSELBEN Liste, sehen
    // aber nur ihre eigenen Buchungen (Server-Scope) und keine Bulk-Assign-
    // Steuerung. Die BookingDetailModal öffnet sich normal beim Zeilenklick
    // und ermöglicht das Abhaken von Aufgaben — genau das, was der User
    // sich gewünscht hat.
    // Frühere „Nur für Admins"-Blockade ist entfernt.
  }

  return (
    <div
      className="max-w-[1400px] mx-auto px-6 md:px-10 py-8 md:py-12 pb-32"
      data-testid="team-bookings-page"
      data-role={user?.role || "guest"}
    >
      <div className="mb-6 flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="label-caps mb-2" style={{ color: BRAND_COLORS.gold }}>Weissblom · Team</div>
          <h1 className="font-display text-4xl md:text-5xl text-navy">
            {isAdmin ? "Buchungen" : "Meine Buchungen"}
          </h1>
          <p className="text-[13.5px] text-navy-dark/65 mt-2 max-w-2xl leading-relaxed">
            {isAdmin
              ? "Übersicht aller Abwesenheiten mit Verantwortlichen-Zuweisung. Standardmäßig werden nur aktive und zukünftige Buchungen angezeigt — vergangene und stornierte Ansichten liegen als separate Chips oben rechts."
              : "Alle Abwesenheiten, die dir direkt zugewiesen sind oder auf einem deiner betreuten Objekte laufen. Zum Bearbeiten und Abhaken einfach eine Zeile antippen."}
          </p>
        </div>
        <div className="flex flex-col items-end gap-1">
          <span className="label-caps">Suche</span>
          <div className="flex items-center gap-2 flex-wrap justify-end">
            <div className="relative">
              <Search className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-navy-dark/40" />
              <input
                type="text"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Kunde · Objekt · Datum"
                data-testid="team-bookings-search"
                className="border border-navy/20 pl-9 pr-3 py-2 text-[13px] w-72 focus:border-gold focus:outline-none"
              />
            </div>
            <button
              type="button"
              onClick={exportCsv}
              disabled={!filtered || filtered.length === 0}
              data-testid="team-bookings-export-csv"
              className="inline-flex items-center gap-1.5 border border-navy/20 hover:border-gold hover:text-gold-dark text-navy px-3 py-2 text-[11px] tracking-[0.15em] uppercase transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
              title="Aktuell gefilterte Buchungen als CSV herunterladen (Excel-kompatibel, ; als Trenner)"
            >
              <Download className="w-3.5 h-3.5" strokeWidth={1.8} />
              CSV Export
            </button>
          </div>
        </div>
      </div>

      {/* Feb 2026 · 6 Sep 2026 · Angefragt-Prompt: nur fuer Admin, nur wenn
          es unassignierte Buchungen gibt und die „Neu / Angefragt"-Kachel
          nicht bereits aktiv ist. Ein-Klick-Sprung zur Status-Kachel. */}
      {isAdmin && unassignedCount > 0 && filterMode !== "requested" && (
        <button
          type="button"
          onClick={() => { setFilterMode("requested"); clearSelection(); }}
          data-testid="team-bookings-unassigned-prompt"
          className="mb-3 w-full sm:w-auto inline-flex items-center gap-3 pl-3 pr-4 py-2 border transition-colors hover:opacity-90 group"
          style={{
            borderColor: withAlpha(STATUS_COLORS.grey, 1),
            background: withAlpha(STATUS_COLORS.grey, 0.4),
          }}
        >
          <span
            className="inline-flex items-center justify-center w-6 h-6 text-[11px] font-semibold tabular-nums"
            style={{ background: BRAND_COLORS.navy, color: BRAND_COLORS.ivory }}
            data-testid="team-bookings-unassigned-count"
          >
            {unassignedCount}
          </span>
          <span className="text-[12.5px] text-navy leading-tight">
            {unassignedCount === 1
              ? "unassignierte Buchung"
              : "unassignierte Buchungen"}
            <span className="text-navy-dark/60"> — jetzt Concierge zuteilen</span>
          </span>
          <span
            className="text-[10px] tracking-[0.18em] uppercase text-navy-dark/60 group-hover:text-navy ml-auto sm:ml-2"
          >
            Zur Kachel →
          </span>
        </button>
      )}

      {/* Filter-Chips · Feb 2026 · Directive #308 · 3 exklusive Modi */}
      <div className="mb-4 flex items-center gap-2 flex-wrap" data-testid="team-bookings-filter-bar">
        <FilterChip
          testid="team-bookings-filter-upcoming"
          active={filterMode === "upcoming"}
          onClick={() => { setFilterMode("upcoming"); clearSelection(); }}
          count={upcomingCount}
          label="Aktive & zukünftige"
          accent={STATUS_COLORS.green}
          icon={<CalendarCheck className="w-3.5 h-3.5" strokeWidth={1.8} />}
        />
        <FilterChip
          testid="team-bookings-filter-past"
          active={filterMode === "past"}
          onClick={() => { setFilterMode("past"); clearSelection(); }}
          count={pastCount}
          label="Vergangene"
          accent={withAlpha(BRAND_COLORS.navy, 0.55)}
          icon={<History className="w-3.5 h-3.5" strokeWidth={1.8} />}
        />
        {/* Feb 2026 · Directive „Status-Kacheln" (1 Sep 2026):
            Drei zusätzliche Kacheln, die exklusiv nach Status filtern.
            Farbcode entspricht dem systemweiten Status-Schema:
              requested   → Grau  (STATUS_COLORS.grey)
              in_progress → Blau  (STATUS_COLORS.blue)
              completed   → Grün  (STATUS_COLORS.green)  */}
        <FilterChip
          testid="team-bookings-filter-requested"
          active={filterMode === "requested"}
          onClick={() => { setFilterMode("requested"); clearSelection(); }}
          count={requestedCount}
          label="Neu / Angefragt"
          accent={STATUS_COLORS.grey}
          icon={<CircleDashed className="w-3.5 h-3.5" strokeWidth={1.8} />}
        />
        <FilterChip
          testid="team-bookings-filter-in-progress"
          active={filterMode === "in_progress"}
          onClick={() => { setFilterMode("in_progress"); clearSelection(); }}
          count={inProgressCount}
          label="In Betreuung"
          accent={STATUS_COLORS.blue}
          icon={<Loader2 className="w-3.5 h-3.5" strokeWidth={1.8} />}
        />
        <FilterChip
          testid="team-bookings-filter-completed"
          active={filterMode === "completed"}
          onClick={() => { setFilterMode("completed"); clearSelection(); }}
          count={completedCount}
          label="Erledigt"
          accent={STATUS_COLORS.green}
          icon={<CheckCircle2 className="w-3.5 h-3.5" strokeWidth={1.8} />}
        />
        <SeenFilterToggle active={unseenOnly} count={unseenCount} onToggle={toggleUnseen} testidBase="team-bookings-unseen" />
        {/* Feb 2026 · 11 Sep 2026 · „Meine Buchungen"-Modifier-Chip.
            Kein exklusiver Status-Modus — schneidet den aktiven Filter
            zusätzlich auf die eigenen zugewiesenen Buchungen ein.
            Sichtbar nur wenn eine ID vorhanden ist (echter Concierge,
            Admin-Selbst-View, Admin-in-Impersonation). Rendert sich mit
            einem leichten Divider davor, damit das Auge den Modifier-
            Charakter vom Status-Set optisch trennt. */}
        {myFilterId && (
          <>
            <span
              aria-hidden="true"
              className="mx-1 h-5 w-px bg-navy/15"
            />
            <FilterChip
              testid="team-bookings-filter-mine"
              active={myOnly}
              onClick={() => { setMyOnly((v) => !v); clearSelection(); }}
              count={myBookingsCount}
              label="Meine Buchungen"
              accent={BRAND_COLORS.gold}
              icon={<UserCheck className="w-3.5 h-3.5" strokeWidth={1.8} />}
            />
          </>
        )}
        {filterMode === "past" && (
          <div
            className="inline-flex items-center gap-2 pl-2 border-l border-navy/10"
            data-testid="team-bookings-past-period"
          >
            <span className="text-[10.5px] tracking-[0.14em] uppercase text-navy-dark/50">Zeitraum</span>
            <select
              value={periodYear}
              onChange={(e) => setPeriodYear(e.target.value)}
              data-testid="team-bookings-past-year"
              className="bg-white border border-navy/20 focus:border-gold focus:outline-none px-2 py-1 text-[12px] text-navy"
            >
              <option value="all">Alle Jahre</option>
              {availableYears.map((y) => (
                <option key={y} value={y}>{y}</option>
              ))}
            </select>
            <select
              value={periodQuarter}
              onChange={(e) => setPeriodQuarter(e.target.value)}
              data-testid="team-bookings-past-quarter"
              className="bg-white border border-navy/20 focus:border-gold focus:outline-none px-2 py-1 text-[12px] text-navy"
              disabled={periodYear === "all"}
              title={periodYear === "all" ? "Zuerst ein Jahr wählen" : "Quartal wählen"}
            >
              <option value="all">Ganzes Jahr</option>
              <option value="Q1">Q1 (Jan-Mär)</option>
              <option value="Q2">Q2 (Apr-Jun)</option>
              <option value="Q3">Q3 (Jul-Sep)</option>
              <option value="Q4">Q4 (Okt-Dez)</option>
            </select>
            {(periodYear !== "all" || periodQuarter !== "all") && (
              <button
                type="button"
                onClick={() => { setPeriodYear("all"); setPeriodQuarter("all"); }}
                data-testid="team-bookings-past-reset"
                className="text-[10.5px] tracking-[0.14em] uppercase text-navy-dark/55 hover:text-navy"
                title="Zeitraum-Filter zurücksetzen"
              >
                Zurücksetzen
              </button>
            )}
          </div>
        )}
        <FilterChip
          testid="team-bookings-filter-cancelled"
          active={filterMode === "cancelled"}
          onClick={() => { setFilterMode("cancelled"); clearSelection(); }}
          count={cancelledCount}
          label="Stornierte"
          accent={STATUS_COLORS.red}
          icon={<Ban className="w-3.5 h-3.5" strokeWidth={1.8} />}
        />
        {(filtered?.length || 0) > 0 && filterMode === "upcoming" && (
          <>
            <span className="text-navy-dark/25">·</span>
            <button
              type="button"
              onClick={selectAllVisible}
              data-testid="team-bookings-select-all"
              className="text-[11px] tracking-[0.14em] uppercase text-navy border-b border-gold hover:text-gold"
            >
              Alle sichtbaren wählen
            </button>
            {selectedIds.size > 0 && (
              <button
                type="button"
                onClick={clearSelection}
                data-testid="team-bookings-clear-selection"
                className="text-[11px] tracking-[0.14em] uppercase text-navy-dark/60 hover:text-navy"
              >
                Auswahl aufheben ({selectedIds.size})
              </button>
            )}
          </>
        )}
      </div>

      {/* Bookings list */}
      {filtered === null ? (
        <div className="text-[13px] text-navy-dark/50 italic" data-testid="team-bookings-loading">
          Lade Buchungen …
        </div>
      ) : filtered.length === 0 ? (
        <div
          className="p-8 text-center border border-dashed border-navy/20 text-[13px] text-navy-dark/60"
          data-testid="team-bookings-empty"
        >
          {filterMode === "cancelled"
            ? (q ? `Keine stornierten Buchungen für „${q}".` : "Keine stornierten Buchungen.")
            : filterMode === "requested"
              ? (q ? `Keine angefragten Buchungen für „${q}".` : "Keine angefragten Buchungen.")
              : filterMode === "in_progress"
                ? (q ? `Keine Buchungen in Betreuung für „${q}".` : "Keine Buchungen in Betreuung.")
                : filterMode === "completed"
                  ? (q ? `Keine erledigten Buchungen für „${q}".` : "Keine erledigten Buchungen.")
                  : filterMode === "past"
                    ? (q ? `Keine vergangenen Buchungen für „${q}".` : "Keine vergangenen Buchungen.")
                    : (q ? `Keine aktiven Buchungen für „${q}".` : "Keine aktiven oder zukünftigen Buchungen.")}
        </div>
      ) : (
        <div className="bg-white border border-navy/10" data-testid="team-bookings-list">
          {filtered.map((b) => {
            const isCancelled = b.status === "cancelled";
            // Feb 2026 · 11 Sep 2026 · Effective-Assignee wie im Detail-
            // Modal: direkte Buchungs-Zuweisung > Haupt-Concierge des
            // Objekts > „Kein Verantwortlicher". Vorher zeigte die Liste
            // fälschlich „Kein Verantwortlicher", auch wenn das Objekt
            // einen Haupt-Concierge hatte.
            const propHaupt = propertiesById[b.property_id]?.assigned_concierge_id || null;
            // Feb 2026 · 11 Sep 2026 · Hotfix Verantwortlich-Anzeige.
            // User-Directive: „VERANTWORTLICH zeigt immer den direkt der
            // Buchung zugewiesenen Verantwortlichen. Nur wenn kein
            // eigener Verantwortlicher zugewiesen ist, darf als Fallback
            // der Haupt-Concierge des Objekts angezeigt werden — mit
            // dem Hinweis (VIA OBJEKT)."
            //
            // Vorher: `directAssignee` wurde direkt aus `membersById`
            // aufgelöst — war der Assignee kein Team-Member mehr (Rolle
            // gewechselt, User gelöscht, /team/members noch nicht
            // geladen) landete `directAssignee = undefined` und die
            // Anzeige rutschte fälschlich in den Property-Fallback.
            //
            // Jetzt: `rawAssigneeId = b.assigned_to` steuert die
            // Verzweigung. Nur wenn WIRKLICH kein assigned_to gesetzt
            // ist, greift der Property-Fallback. Fehlt nur der Name,
            // zeigen wir stattdessen „Unbekannter Benutzer" — nie mehr
            // fälschlich den Haupt-Concierge.
            const rawAssigneeId = b.assigned_to || null;
            const directAssignee = rawAssigneeId
              ? (membersById[rawAssigneeId] || null)
              : null;
            const isSelected = selectedIds.has(b.id);
            // Feb 2026 · Directive #311 · Buchungsart:
            //   "absence"        → Abwesenheit (native Booking)
            //   "extra_attached" → Extrabuchung zu einer Absence
            //   "extra_separate" → Standalone-Buchung mit Wunschdatum
            const kind = b.booking_kind || "absence";
            const isAbsence = kind === "absence";
            // Nur Abwesenheiten können Bulk-Assigned werden (die Extras
            // erben ihren Verantwortlichen bereits aus der Parent-Absence
            // bzw. sind an dem Request-Owner geheftet).
            const canBulkSelect = isAbsence && !isCancelled;
            // Feb 2026 · Systemweiter Farbcode für Buchungs-Status
            // (Grau/Blau/Grün/Rot gestreift diagonal) — kanonisch aus
            // `bookingPill` (Abwesenheit) bzw. `taskStatusColor` (Extras).
            // Der Left-Border-Stripe verwendet für Storniert einen
            // repeating-linear-gradient (135deg) — konsistent mit dem
            // Kalender (siehe `TeamCalendar.jsx` L732).
            const pill = rowStatusPill(b, isAbsence);
            // Klick-Handling · Feb 2026 · User-Directive „Klick öffnet
            // Buchungsdetail, NIE Kundenprofil":
            //   • Abwesenheit → BookingDetailModal
            //   • Extra mit Parent-Absence → BookingDetailModal des Parents
            //   • Extra standalone / Request-Anything / Team-Task /
            //     Buchungs-Änderung → RequestDetailModal (lazy-load per
            //     sr_request_ids[0]). Kein `window.location.href` mehr auf
            //     /team/customers/{id} — der Concierge muss nicht durch die
            //     Kundenverwaltung navigieren, um seine Aufgabe zu öffnen.
            const handleRowOpen = async () => {
              if (isAbsence) { setSelectedBooking(b); return; }
              const parent = b.parent_booking_id
                ? rows.find((r) => r.id === b.parent_booking_id)
                : null;
              if (parent) { setSelectedBooking(parent); return; }
              const reqIds = Array.isArray(b.sr_request_ids) ? b.sr_request_ids : [];
              const firstReqId = reqIds[0];
              if (!firstReqId) { toast.error("Details für diese Position nicht verfügbar."); return; }
              try {
                const { data } = await api.get("/requests");
                const found = (data || []).find((r) => r.id === firstReqId);
                if (found) setSelectedRequest(found);
                else toast.error("Zugehörige Anfrage nicht gefunden.");
              } catch (err) {
                toast.error(formatErr(err.response?.data?.detail) || err.message);
              }
            };
            return (
              <div
                key={b.id}
                data-testid={`team-booking-row-${b.id}`}
                data-selected={isSelected ? "true" : "false"}
                data-assigned={b.assigned_to ? "true" : "false"}
                data-cancelled={isCancelled ? "true" : "false"}
                data-kind={kind}
                data-status={b.status || "requested"}
                role="button"
                tabIndex={0}
                onClick={handleRowOpen}
                onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); handleRowOpen(); } }}
                className={`relative flex items-center gap-4 pl-6 pr-4 md:pr-6 py-4 border-b border-navy/5 last:border-b-0 transition-colors cursor-pointer ${
                  isCancelled
                    ? "opacity-70 hover:bg-navy/[0.02]"
                    : (isSelected ? "bg-gold/5" : "hover:bg-navy/[0.02]")
                }`}
                title="Buchungs-Details öffnen"
              >
                {/* Systemweiter Status-Farbstreifen links (4 px).
                    Grau = Angefragt · Blau = In Betreuung · Grün = Erledigt ·
                    Rot diagonal gestreift = Storniert. */}
                <span
                  aria-hidden="true"
                  data-testid={`team-booking-stripe-${b.id}`}
                  className="absolute left-0 top-0 bottom-0 w-1"
                  style={{ background: pill.bg }}
                />
                {isCancelled || !canBulkSelect || !isAdmin ? (
                  <div
                    aria-hidden="true"
                    data-testid={`team-booking-marker-${b.id}`}
                    className="w-5 h-5 shrink-0 flex items-center justify-center text-navy-dark/40"
                    title={isCancelled ? "Storniert — keine Auswahl möglich" : (!isAdmin ? "Bulk-Zuweisung ist Admin-only" : "Extras nutzen den Bulk-Assign nicht")}
                  >
                    {isCancelled ? <X className="w-4 h-4" strokeWidth={2} /> : null}
                  </div>
                ) : (
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); toggle(b.id); }}
                    data-testid={`team-booking-check-${b.id}`}
                    aria-pressed={isSelected}
                    className="w-5 h-5 rounded-md border-2 flex items-center justify-center transition-all shrink-0"
                    style={isSelected
                      ? { background: BRAND_COLORS.gold, borderColor: BRAND_COLORS.gold }
                      : { background: BRAND_COLORS.ivory, borderColor: withAlpha(BRAND_COLORS.navy, 0.3) }
                    }
                  >
                    {isSelected && <Check className="w-3.5 h-3.5 text-navy" strokeWidth={3} />}
                  </button>
                )}

                <div className={`flex-1 min-w-0 grid grid-cols-1 md:grid-cols-4 gap-x-4 gap-y-1 ${isCancelled ? "line-through decoration-navy-dark/30" : ""}`}>
                  <div className="min-w-0">
                    {/* Feb 2026 · User-Directive „Titel in Listenzeile":
                        direkt an erster Stelle, damit der Admin sofort
                        weiß was für eine Buchung das ist — noch vor Kunde
                        und Objekt, weil Titel = Hauptzweck. */}
                    <div
                      className="font-display text-[15px] md:text-[16px] text-navy leading-tight truncate"
                      data-testid={`team-booking-title-${b.id}`}
                      title={deriveBookingTitle(b)}
                    >
                      {deriveBookingTitle(b)}
                    </div>
                    <div className="text-[12.5px] text-navy-dark/75 truncate mt-0.5">
                      {b.customer_name || b.customer_email || "—"}
                    </div>
                    <div className="flex items-center gap-1 text-[11.5px] text-navy-dark/60 truncate">
                      <MapPin className="w-3 h-3 shrink-0" />
                      {b.property_name || "—"}
                    </div>
                    <BookingKindBadge kind={kind} testid={`team-booking-kind-${b.id}`} />
                  </div>
                  <div className="min-w-0">
                    <div className="flex items-center gap-1 text-[12px] text-navy">
                      <CalIcon className="w-3 h-3" />
                      <span className="tabular-nums">
                        {b.from_date ? format(parseISO(b.from_date), "dd. MMM yy", { locale: de }) : "—"}
                        {" → "}
                        {b.to_date ? format(parseISO(b.to_date), "dd. MMM yy", { locale: de }) : "—"}
                      </span>
                    </div>
                    {/* Feb 2026 · Systemweiter Status-Pill (Grau/Blau/Grün/
                        Rot gestreift). Ersetzt den früheren rohen Status-
                        Text — deckungsgleich mit Kalender & Detail-Modal. */}
                    <div className="mt-1 flex items-center gap-1.5">
                      <SeenDot entity={b} viewerId={myFilterId} testid={`team-booking-seen-${b.id}`} />
                      <StatusBadge
                        colorKey={hexToStatusKey(pill.bg)}
                        label={pill.label}
                        striped={isCancelled}
                        testid={`team-booking-status-${b.id}`}
                      />
                    </div>
                  </div>
                  <div className="min-w-0 md:col-span-2">
                    {/* Feb 2026 · 11 Sep 2026 · Zweizeiler in der Buchungs-
                        liste — vollständige UX-Konsistenz zum BookingDetail-
                        Modal-Header (siehe BookingHeaderAssigneeLines).
                        Zeile 1: Haupt-Concierge des Objekts (Fallback).
                        Zeile 2: Verantwortlicher der Buchung.
                        Beide immer sichtbar, auch wenn identisch. */}
                    {isCancelled && !directAssignee && !propHaupt ? (
                      // Storniert ohne jede Zuweisung → dezenter Hinweis
                      // statt roter Warnung. Stornierte Buchungen brauchen
                      // keinen Verantwortlichen.
                      <div
                        className="text-[11.5px] italic text-navy-dark/45"
                        data-testid={`team-booking-cancelled-note-${b.id}`}
                      >
                        Storniert
                      </div>
                    ) : (
                      <div
                        className="flex items-start gap-1.5"
                        data-testid={`team-booking-assignee-lines-${b.id}`}
                        data-assignee-source={rawAssigneeId ? "booking" : (propHaupt ? "property" : "none")}
                        data-assignee-id={rawAssigneeId || ""}
                      >
                        <UserCheck
                          className="w-3.5 h-3.5 mt-0.5 shrink-0"
                          style={{ color: BRAND_COLORS.gold }}
                          strokeWidth={1.6}
                        />
                        <div className="min-w-0 flex flex-col gap-0.5">
                          {/* Zeile 1 · Haupt-Concierge des Objekts */}
                          <div
                            className="text-[11.5px] leading-tight truncate"
                            data-testid={`team-booking-haupt-concierge-${b.id}`}
                          >
                            <span className="tracking-[0.12em] uppercase text-navy-dark/50 mr-1">
                              Haupt-Concierge ·{" "}
                            </span>
                            {propHaupt && membersById[propHaupt] ? (
                              <span className="text-navy-dark/85">
                                {membersById[propHaupt].name || membersById[propHaupt].email}
                              </span>
                            ) : (
                              <span className="text-navy-dark/45 italic">
                                — (nicht zugewiesen)
                              </span>
                            )}
                          </div>
                          {/* Zeile 2 · Verantwortlicher der Buchung.
                              Verzweigung strikt an `rawAssigneeId` (nicht
                              am aufgelösten Member) — sonst zeigen wir
                              fälschlich den Haupt-Concierge mit
                              „(via Objekt)", obwohl die Buchung sehr wohl
                              einen eigenen Verantwortlichen hat. */}
                          <div
                            className="text-[11.5px] leading-tight truncate"
                            data-testid={`team-booking-responsible-${b.id}`}
                          >
                            <span className="tracking-[0.12em] uppercase text-navy-dark/50 mr-1">
                              Verantwortlich ·{" "}
                            </span>
                            {rawAssigneeId ? (
                              <span className="text-navy font-medium">
                                {directAssignee ? (
                                  directAssignee.name || directAssignee.email
                                ) : (
                                  <span className="italic text-navy-dark/60">
                                    Unbekannter Benutzer
                                  </span>
                                )}
                                {propHaupt && propHaupt === rawAssigneeId && (
                                  <span
                                    className="ml-1.5 text-[10px] tracking-[0.12em] uppercase text-navy-dark/40"
                                    data-testid={`team-booking-responsible-same-${b.id}`}
                                  >
                                    (identisch)
                                  </span>
                                )}
                              </span>
                            ) : propHaupt && membersById[propHaupt] ? (
                              <span className="text-navy-dark/70">
                                {membersById[propHaupt].name || membersById[propHaupt].email}
                                <span
                                  className="ml-1.5 text-[10px] tracking-[0.12em] uppercase text-navy-dark/40"
                                  data-testid={`team-booking-responsible-via-property-${b.id}`}
                                >
                                  (via Objekt)
                                </span>
                              </span>
                            ) : isCancelled ? (
                              <span className="text-navy-dark/45 italic">
                                — (nicht zugewiesen)
                              </span>
                            ) : (
                              <span
                                className="inline-flex items-center gap-1 font-medium"
                                data-testid={`team-booking-unassigned-${b.id}`}
                                style={{ color: STATUS_COLORS.red }}
                              >
                                <AlertTriangle className="w-3 h-3" strokeWidth={1.8} />
                                Kein Verantwortlicher
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
                <Link
                  to={`/team/customers/${b.customer_id}`}
                  onClick={(e) => e.stopPropagation()}
                  data-testid={`team-booking-customer-${b.id}`}
                  className="text-[10.5px] tracking-[0.14em] uppercase text-navy-dark/55 hover:text-gold shrink-0"
                  title="Zum Kundenprofil (nicht Buchungsdetail)"
                >
                  Kunde →
                </Link>
              </div>
            );
          })}
        </div>
      )}

      {/* Sticky Bulk-Action-Bar · Admin-only (Bulk-Zuweisung von Verantwortlichen) */}
      {isAdmin && selectedIds.size > 0 && (
        <div
          className="fixed bottom-4 left-1/2 -translate-x-1/2 w-[95%] max-w-3xl bg-white border border-navy shadow-2xl p-4 flex flex-col md:flex-row items-stretch md:items-center gap-3 z-40"
          data-testid="team-bookings-bulk-bar"
        >
          <div className="text-[13px] text-navy min-w-0 flex-1">
            <strong>{selectedIds.size}</strong> {selectedIds.size === 1 ? "Buchung" : "Buchungen"} ausgewählt · Verantwortlicher zuweisen:
          </div>
          <select
            value={pickAssignee}
            onChange={(e) => setPickAssignee(e.target.value)}
            data-testid="team-bookings-bulk-assignee"
            className="bg-white border border-navy/20 focus:border-gold focus:outline-none px-3 py-2 text-[13px] text-navy min-w-[220px]"
          >
            <option value="">Verantwortlichen wählen …</option>
            {members.map((m) => (
              <option key={m.id} value={m.id}>
                {m.name || m.email} · {m.role === "admin" ? "Admin" : "Concierge"}
              </option>
            ))}
          </select>
          <button
            type="button"
            onClick={submitBulk}
            disabled={busy || !pickAssignee}
            data-testid="team-bookings-bulk-submit"
            className="inline-flex items-center gap-2 text-ivory px-5 py-2.5 text-[11px] tracking-[0.15em] uppercase hover:opacity-90 disabled:opacity-40"
            style={{ background: BRAND_COLORS.navy }}
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <UserCheck className="w-4 h-4" />}
            Zuweisen
          </button>
          <button
            type="button"
            onClick={clearSelection}
            disabled={busy}
            data-testid="team-bookings-bulk-cancel"
            className="text-[11px] tracking-[0.15em] uppercase text-navy-dark/60 hover:text-navy px-2 py-2"
          >
            Abbrechen
          </button>
        </div>
      )}

      {bulkPinOpen && (
        <AdminPinPromptDialog
          title="Bulk-Zuweisung bestätigen"
          description={`Bitte den 4-stelligen Admin-PIN eingeben, um ${selectedIds.size} ${selectedIds.size === 1 ? "Buchung" : "Buchungen"} zuzuweisen.`}
          purpose="booking-bulk-assign"
          onClose={() => setBulkPinOpen(false)}
          onSuccess={() => {
            setBulkPinOpen(false);
            setBulkPinUnlocked(true);
            setTimeout(() => submitBulk(), 0);
          }}
        />
      )}

      {/* Feb 2026 · Directive #309 · Buchungs-Detailmodal (Team-Modus).
          Öffnet sich per Row-Klick und zeigt alle relevanten Informationen
          — inkl. Standard-Services + gebuchte Extras + Storno-Historie. */}
      {selectedBooking && (
        <BookingDetailModal
          booking={selectedBooking}
          extras={extrasCatalog}
          propertyName={selectedBooking.property_name || ""}
          mode="team"
          onClose={() => setSelectedBooking(null)}
          onChanged={() => { load().catch(() => {}); }}
        />
      )}
      {/* Feb 2026 · Non-Absence-Rows (Request-Anything, Team-Task, Extras,
          Buchungs-Änderungen) öffnen den RequestDetailModal — deckt die
          User-Directive „Klick öffnet Buchungsdetail, nicht Kundenprofil"
          konsistent auch für diese Task-Rows ab. */}
      {selectedRequest && (
        <RequestDetailModal
          request={selectedRequest}
          onClose={() => setSelectedRequest(null)}
          onLinkedChange={() => { setSelectedRequest(null); load().catch(() => {}); }}
        />
      )}
    </div>
  );
}

// Feb 2026 · Directive · Systemweiter Status-Farbcode in der Buchungs-
// liste. Native Abwesenheiten nutzen `bookingPill` (Grau→Blau-Regel mit
// Assignee-Check, dieselbe Truth-Source wie Kalender + Detail-Modal).
// Extras kollabieren ihren Task-Status auf eine der 4 Systemfarben via
// `taskStatusColor`, dazu bekommen sie deutsche Labels aus der Map unten.
const EXTRA_STATUS_LABEL = {
  new: "Neu",
  received: "Eingegangen",
  requested: "Angefragt",
  pending: "Ausstehend",
  awaiting_approval: "Wartet auf Freigabe",
  under_review: "In Prüfung",
  quote_required: "Angebot benötigt",
  scheduled: "Terminiert",
  in_progress: "In Bearbeitung",
  in_service: "In Betreuung",
  confirmed: "Bestätigt",
  accepted: "Bestätigt",
  active: "Aktiv",
  done: "Erledigt",
  completed: "Erledigt",
  closed: "Erledigt",
  cancelled: "Storniert",
  overdue: "Überfällig",
};
function rowStatusPill(booking, isAbsence) {
  if (isAbsence) return bookingPill(booking);
  const c = taskStatusColor(booking?.status);
  const label = EXTRA_STATUS_LABEL[booking?.status] || (booking?.status || "—");
  return { bg: c.bg, fg: c.fg, label };
}

// Feb 2026 · Directive #311 · Buchungsart-Badge (Row-Label).
// Mapping:
//   absence         → Abwesenheit  (Navy · Standard-Abwesenheit)
//   extra_attached  → Extrabuchung (Gold · Zusatzleistung zu einer Absence)
//   extra_separate  → Separate Buchung (Grau/Gold-Rand · Standalone-Bestellung)
// Wird in `TeamBookings.jsx` unter Kunde/Objekt je Row angezeigt.
const BOOKING_KIND_META = {
  absence:        { label: "Abwesenheit",     bg: withAlpha(BRAND_COLORS.navy, 0.08),   fg: BRAND_COLORS.navy,     border: withAlpha(BRAND_COLORS.navy, 0.25) },
  extra_attached: { label: "Extrabuchung",    bg: withAlpha(BRAND_COLORS.gold, 0.14),   fg: BRAND_COLORS.goldDark, border: withAlpha(BRAND_COLORS.gold, 0.45) },
  extra_separate: { label: "Separate Buchung", bg: withAlpha(BRAND_COLORS.gold, 0.06),  fg: BRAND_COLORS.goldDark, border: withAlpha(BRAND_COLORS.gold, 0.55) },
  // Feb 2026 · User-Directive „Meine offenen Aufgaben zeigt alles":
  // eigene Labels/Farbtöne für die drei zusätzlichen Task-Sources.
  request_task:   { label: "Anfrage",          bg: withAlpha(BRAND_COLORS.navy, 0.08),   fg: BRAND_COLORS.navyDark, border: withAlpha(BRAND_COLORS.navy, 0.35) },
  team_task:      { label: "Team-Aufgabe",     bg: withAlpha(BRAND_COLORS.navy, 0.06),   fg: BRAND_COLORS.navy,     border: withAlpha(BRAND_COLORS.navy, 0.30) },
  change_request: { label: "Änderung",         bg: withAlpha(BRAND_COLORS.gold, 0.10),   fg: BRAND_COLORS.goldDark, border: withAlpha(BRAND_COLORS.gold, 0.40) },
};
function BookingKindBadge({ kind, testid }) {
  const meta = BOOKING_KIND_META[kind] || BOOKING_KIND_META.absence;
  return (
    <span
      data-testid={testid}
      className="inline-block mt-1 px-1.5 py-0.5 text-[9.5px] tracking-[0.14em] uppercase rounded-sm"
      style={{ background: meta.bg, color: meta.fg, border: `1px solid ${meta.border}` }}
    >
      {meta.label}
    </span>
  );
}

// Feb 2026 · Directive #311 · Filter-Chip für die drei Modi (Aktiv / Ohne
// Verantwortlichen / Storniert). Aktiver Chip wird in seiner Akzentfarbe
// hervorgehoben, inaktive haben nur einen dezenten Border.
function FilterChip({ testid, active, onClick, count, label, accent, icon }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      data-active={active ? "true" : "false"}
      className="inline-flex items-center gap-2 px-3 py-1.5 text-[11px] tracking-[0.14em] uppercase rounded-full transition-colors"
      style={active
        ? { background: accent, color: BRAND_COLORS.ivory, border: `1px solid ${accent}` }
        : { background: "white", color: BRAND_COLORS.navy, border: "1px solid rgba(11,42,64,0.15)" }}
    >
      {icon}
      {label}
      <span
        className="inline-flex items-center justify-center min-w-[22px] h-[18px] px-1.5 rounded-full text-[10.5px] font-semibold tabular-nums"
        style={active
          ? { background: "rgba(255,255,255,0.22)", color: BRAND_COLORS.ivory }
          : { background: withAlpha(BRAND_COLORS.navy, 0.08), color: BRAND_COLORS.navy }}
      >
        {count}
      </span>
    </button>
  );
}
